import { useEffect, useMemo, useRef, useState, memo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _isEqual from 'lodash/isEqual';
import _kebabCase from 'lodash/kebabCase';
import _isUndefined from 'lodash/isUndefined';
import * as analytics from '@benniehealth/core-js/analytics';
import profile from '@benniehealth/core-js/store/profile';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useLDUser from '@app/hooks/useLDUser';
import {
  XMarkIcon,
  Squares2X2Icon,
  EnvelopeIcon,
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  UserGroupIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/solid';

import { styled, useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SvgIcon, Icon, Box, Skeleton } from '@mui/material';
import Hidden from '@app/components/Hidden';
import { DRAWER_WIDTH, COLORS } from '@benniehealth/theme';

import withRouter from '@app/containers/WithRouter';
import { NAVIGATION as NAV_CONSTANTS } from '@app/constants';
import withClickTracking from '@app/containers/WithClickTracking';
import { useTranslation } from '@app/i18n';
import BennieWhiteLogoSvg from '@app/images/svg/logo_bennie_white.svg';

import NavButton from './NavButton';
import NavSkeleton from './NavSkeleton';

const PREFIX = 'index';

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerInner: `${PREFIX}-drawerInner`,
  drawerOpen: `${PREFIX}-drawerOpen`,
  logo: `${PREFIX}-logo`,
  closeIconWrapper: `${PREFIX}-closeIconWrapper`,
  closeIcon: `${PREFIX}-closeIcon`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`&.${classes.drawer}`]: {
    '& .nav-list-item': {
      color: theme.palette.common.white,
      paddingLeft: theme.spacing(3),
      [theme.breakpoints.down('md')]: {
        paddingLeft: theme.spacing(2),
      },
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
        '& .nav-icon-wrapper': {
          '& svg': {
            color: theme.palette.common.white,
          },
        },
      },
      '&.active': {
        backgroundColor: theme.palette.primary.light,
        '& .nav-icon-wrapper': {
          '& svg': {
            color: theme.palette.common.white,
          },
        },
      },
      '& .MuiListItemText-root': {
        paddingLeft: theme.spacing(2),
      },
      '& .MuiTouchRipple-root': {
        display: 'none',
        height: `auto`,
      },
      '&.hidden-mobile': {
        [theme.breakpoints.down('sm')]: {
          display: `none`,
        },
      },
    },
    '& .nav-icon-wrapper': {
      width: '24px',
      height: '24px',
      '& svg': {
        width: '100%',
        color: theme.palette.customGrey.silverDark,
      },
    },
    '& .nav-icon': {
      [theme.breakpoints.up('md')]: {
        marginLeft: 0,
      },
    },
  },
  [`&.${classes.drawerOpen}, & .${classes.drawerOpen}`]: {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
  },
  [`& .${classes.drawerInner}`]: {
    backgroundColor: COLORS.NAVY_BLUE_DARK,
    '& .MuiList-root': {
      [theme.breakpoints.down('sm')]: {
        paddingTop: `0`,
      },
    },
  },
  [`& .${classes.logo}`]: {
    width: `100px`,
    height: `auto`,
    marginBottom: '32px',
    marginLeft: theme.spacing(3),
    paddingTop: '45px',
    '&.MuiSvgIcon-root': {
      width: `100px`,
      height: `auto`,
    },
  },
  [`& .${classes.closeIconWrapper}`]: {
    marginBottom: `66px`,
    width: `24px`,
    height: `56px`,
    transition: `all 0.2s easeOut`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: `0`,
    },
  },
  [`& .${classes.closeIcon}`]: {
    width: `24px`,
    height: `auto`,
    color: theme.palette.common.white,
    cursor: 'pointer',
    marginLeft: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(2),
    },
    '& svg': {
      width: '100%',
    },
  },
}));

const { SCREENS, INTRO_TOUR_MY_BENEFITS_NAV_DOM_ID } = NAV_CONSTANTS;
const TrackingNavButton = withClickTracking(NavButton);

NavButton.propTypes = {
  link: PropTypes.string.isRequired,
  icon: PropTypes.object,
  name: PropTypes.string.isRequired,
};

const getScreenNames = t => {
  return {
    [SCREENS.HOME]: t(`navigation.home`),
    [SCREENS.MESSAGES]: t(`navigation.messages`),
    [SCREENS.MARKETPLACE]: t(`navigation.marketplace`),
    [SCREENS.FIND_A_DOCTOR]: t(`navigation.findMedicalCare`),
    [SCREENS.ACCOUNT]: t(`navigation.account`),
    [SCREENS.ADMIN]: t(`navigation.admin`),
    [SCREENS.LOGOUT]: t(`navigation.logout`),
  };
};

const prioritizeAdminNavItem = (navItems, isHrAdmin, isSystemAdmin) => {
  if (isHrAdmin || isSystemAdmin) {
    const adminItem = navItems.find(item => item.key === SCREENS.ADMIN);
    if (adminItem) {
      return [
        adminItem,
        ...navItems.filter(item => item.key !== SCREENS.ADMIN),
      ];
    }
  }
  return navItems;
};

const getNavConfig = (flags, isAdmin, isHrAdmin, isSystemAdmin) => {
  const result = [
    {
      key: SCREENS.HOME,
      link: '/dashboard',
      icon: Squares2X2Icon,
      eventType: analytics.navigation.HOME,
    },
    flags.askBennie && {
      key: SCREENS.MESSAGES,
      link: '/ask-bennie',
      icon: EnvelopeIcon,
      eventType: analytics.navigation.ASK_BENNIE,
      flagName: 'askBennie',
    },
    flags.marketplace && {
      key: SCREENS.MARKETPLACE,
      link: isAdmin ? '/marketplace/tabs/employees' : '/marketplace',
      icon: ShoppingBagIcon,
      eventType: analytics.navigation.MARKETPLACE,
    },
    {
      key: SCREENS.FIND_A_DOCTOR,
      link: '/find-medical-care',
      icon: MagnifyingGlassIcon,
      eventType: analytics.navigation.FIND_A_DOCTOR,
    },
    {
      key: SCREENS.ACCOUNT,
      icon: UserCircleIcon,
      link: '/account',
      eventType: analytics.navigation.ACCOUNT,
    },
    (isHrAdmin || isSystemAdmin) && {
      key: SCREENS.ADMIN,
      icon: UserGroupIcon,
      link: '/admin',
      eventType: analytics.navigation.ADMIN,
    },
    {
      key: SCREENS.LOGOUT,
      icon: ArrowRightStartOnRectangleIcon,
      link: '/logout',
      eventType: analytics.common.LOGOUT,
    },
  ].filter(Boolean);

  return prioritizeAdminNavItem(result, isHrAdmin, isSystemAdmin);
};

const getFullNavConfig = (isAdmin, isHrAdmin, isSystemAdmin) => {
  const result = [
    {
      key: SCREENS.HOME,
      link: '/dashboard',
      icon: Squares2X2Icon,
      eventType: analytics.navigation.HOME,
    },
    {
      key: SCREENS.MESSAGES,
      link: '/ask-bennie',
      icon: EnvelopeIcon,
      eventType: analytics.navigation.ASK_BENNIE,
      flagName: 'askBennie',
      requiresFlag: true,
    },
    {
      key: SCREENS.MARKETPLACE,
      link: isAdmin ? '/marketplace/tabs/employees' : '/marketplace',
      icon: ShoppingBagIcon,
      eventType: analytics.navigation.MARKETPLACE,
      flagName: 'marketplace',
      requiresFlag: true,
    },
    {
      key: SCREENS.FIND_A_DOCTOR,
      link: '/find-medical-care',
      icon: MagnifyingGlassIcon,
      eventType: analytics.navigation.FIND_A_DOCTOR,
    },
    {
      key: SCREENS.ACCOUNT,
      icon: UserCircleIcon,
      link: '/account',
      eventType: analytics.navigation.ACCOUNT,
    },
    (isHrAdmin || isSystemAdmin) && {
      key: SCREENS.ADMIN,
      icon: UserGroupIcon,
      link: '/admin',
      eventType: analytics.navigation.ADMIN,
      requiresRole: true,
    },
    {
      key: SCREENS.LOGOUT,
      icon: ArrowRightStartOnRectangleIcon,
      link: '/logout',
      eventType: analytics.common.LOGOUT,
    },
  ].filter(Boolean);

  return prioritizeAdminNavItem(result, isHrAdmin, isSystemAdmin);
};

export const NavigationComponent = ({
  isHrAdmin,
  isSystemAdmin,
  isAdmin,
  isOpen,
  onClose,
  onOpen,
  location,
  isSigningOut,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [stableRoles, setStableRoles] = useState({
    isHrAdmin: isHrAdmin ?? false,
    isSystemAdmin: isSystemAdmin ?? false,
    isAdmin: isAdmin ?? false,
  });
  const [stableFlags, setStableFlags] = useState({
    askBennie: false,
    marketplace: false,
  });
  const rolesEverReceived = useRef(
    !_isUndefined(isHrAdmin) && !_isUndefined(isSystemAdmin) && !_isUndefined(isAdmin),
  );

  const { isLdUserIdentified } = useLDUser();
  const flags = useFlags();

  useEffect(() => {
    if (!_isUndefined(isHrAdmin) && !_isUndefined(isSystemAdmin) && !_isUndefined(isAdmin)) {
      rolesEverReceived.current = true;
    }
    setStableRoles(prev => ({
      isHrAdmin: _isUndefined(isHrAdmin) ? prev.isHrAdmin : isHrAdmin,
      isSystemAdmin: _isUndefined(isSystemAdmin)
        ? prev.isSystemAdmin
        : isSystemAdmin,
      isAdmin: _isUndefined(isAdmin) ? prev.isAdmin : isAdmin,
    }));
  }, [isAdmin, isHrAdmin, isSystemAdmin]);

  useEffect(() => {
    if (isLdUserIdentified && flags) {
      setStableFlags(prev => {
        const newAskBennie = flags.askBennie ?? prev.askBennie;
        const newMarketplace = flags.marketplace ?? prev.marketplace;
        
        if (
          prev.askBennie === newAskBennie &&
          prev.marketplace === newMarketplace
        ) {
          return prev;
        }
        
        return {
          askBennie: newAskBennie,
          marketplace: newMarketplace,
        };
      });
    }
  }, [isLdUserIdentified, flags?.askBennie, flags?.marketplace]);

  const resolvedIsHrAdmin = stableRoles.isHrAdmin ?? false;
  const resolvedIsSystemAdmin = stableRoles.isSystemAdmin ?? false;
  const resolvedIsAdmin = stableRoles.isAdmin ?? false;

  const flagsReady = isLdUserIdentified && flags !== undefined;
  const rolesReady = rolesEverReceived.current;
  const allDataReady = flagsReady && rolesReady;

  const prevPathname = useRef(location.pathname);
  const currentLocationRef = useRef({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  const navStateRef = useRef({
    _prevLocation: {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
  });

  currentLocationRef.current = {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  };
  navStateRef.current._prevLocation = currentLocationRef.current;

  useEffect(() => {
    if (location.pathname !== prevPathname.current && isOpen) {
      onClose();
    }
    prevPathname.current = location.pathname;
  }, [location.pathname, isOpen, onClose]);

  const screenNames = useMemo(() => getScreenNames(t), [t]);
  const fullNavConfig = useMemo(
    () => getFullNavConfig(resolvedIsAdmin, resolvedIsHrAdmin, resolvedIsSystemAdmin),
    [resolvedIsAdmin, resolvedIsHrAdmin, resolvedIsSystemAdmin],
  );
  const navConfig = useMemo(
    () => (allDataReady ? getNavConfig(stableFlags, resolvedIsAdmin, resolvedIsHrAdmin, resolvedIsSystemAdmin) : []),
    [allDataReady, stableFlags, resolvedIsAdmin, resolvedIsHrAdmin, resolvedIsSystemAdmin],
  );

  const { mobileEmployeeOptions, desktopEmployeeOptions } = useMemo(() => {
    const buildList = myBenefitsIntroTourDomId => (
      <List data-testid="employee-nav">
        {!allDataReady
          ? fullNavConfig.map(item => <NavSkeleton key={item.key} />)
          : navConfig.map(item => (
              <TrackingNavButton
                event={{
                  eventType: analytics.common.getEventType(
                    analytics.navigation.NAVIGATION,
                    item.eventType,
                  ),
                }}
                link={item.link}
                state={navStateRef.current}
                icon={item.icon}
                key={item.key}
                name={screenNames[item.key]}
                data-testid={
                  item.link === '/dashboard'
                    ? 'nav-my-benefits'
                    : `nav-${_kebabCase(item.link)}`
                }
                id={
                  item.link === '/dashboard' && myBenefitsIntroTourDomId
                    ? myBenefitsIntroTourDomId
                    : undefined
                }
                className={
                  (resolvedIsHrAdmin || resolvedIsSystemAdmin) &&
                  _isEqual(item.key, SCREENS.ADMIN)
                    ? 'hidden-mobile'
                    : item.key
                }
              />
            ))}
      </List>
    );

    return {
      mobileEmployeeOptions: buildList(null),
      desktopEmployeeOptions: buildList(INTRO_TOUR_MY_BENEFITS_NAV_DOM_ID),
    };
  }, [
    allDataReady,
    fullNavConfig,
    navConfig,
    screenNames,
    resolvedIsHrAdmin,
    resolvedIsSystemAdmin,
  ]);

  if (isSigningOut) {
    return null;
  }

  return (
    <nav aria-label="navigation">
      <Hidden mdUp>
        <StyledDrawer
          as={SwipeableDrawer}
          onClose={onClose}
          onOpen={onOpen}
          ModalProps={{ keepMounted: true }}
          open={isOpen}
          className={classNames(classes.drawer, {
            [classes.drawerOpen]: true,
          })}
          classes={{
            paper: classNames(classes.drawerInner, {
              [classes.drawerOpen]: true,
            }),
          }}
        >
          <Box
            display="flex"
            justifyContent="flex-start"
            className={classes.closeIconWrapper}
          >
            <Icon
              component={XMarkIcon}
              className={classes.closeIcon}
              onClick={onClose}
            />
          </Box>
          {mobileEmployeeOptions}
        </StyledDrawer>
      </Hidden>

      <Hidden mdDown>
        <StyledDrawer
          variant="permanent"
          className={classNames(classes.drawer, {
            [classes.drawerOpen]: mdUp,
          })}
          classes={{
            paper: classNames(classes.drawerInner, {
              [classes.drawerOpen]: mdUp,
            }),
          }}
        >
          <Link to="/">
            <SvgIcon
              component={BennieWhiteLogoSvg}
              viewBox="0 0 360 114"
              className={classes.logo}
            />
          </Link>
          {desktopEmployeeOptions}
        </StyledDrawer>
      </Hidden>
    </nav>
  );
};

NavigationComponent.propTypes = {
  isHrAdmin: PropTypes.bool,
  isSystemAdmin: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  zendesk: PropTypes.shape({
    unreadMessages: PropTypes.arrayOf(PropTypes.object),
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  isSigningOut: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    isHrAdmin: profile.selectors.getIsHrAdmin(state),
    isSystemAdmin: profile.selectors.getIsSystemAdmin(state),
    isAdmin: profile.selectors.getIsAdmin(state),
    isSigningOut: state.auth.isSigningOut,
  };
};

const ConnectedNavigation = compose(
  withRouter,
  connect(mapStateToProps),
)(NavigationComponent);

const MemoizedNavigation = memo(ConnectedNavigation, (prevProps, nextProps) => {
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.isSigningOut === nextProps.isSigningOut &&
    prevProps.isHrAdmin === nextProps.isHrAdmin &&
    prevProps.isSystemAdmin === nextProps.isSystemAdmin &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.location?.pathname === nextProps.location?.pathname
  );
});

export default MemoizedNavigation;
